import { Calendar, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { TaskStatusBadge } from './TaskStatusBadge';
import { TaskPriorityBadge } from './TaskPriorityBadge';
import { cn } from '@/lib/utils';

interface TaskCardProps {
  task: {
    id: string;
    title: string;
    status: 'todo' | 'in_progress' | 'done' | 'cancelled';
    priority: 'low' | 'medium' | 'high' | 'critical';
    due_date: string | null;
    assignee_count?: number;
  };
  onClick?: () => void;
  onToggle?: (checked: boolean) => void;
  className?: string;
}

export function TaskCard({ task, onClick, onToggle, className }: TaskCardProps) {
  const isDone = task.status === 'done';
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !isDone;

  return (
    <Card
      className={cn('cursor-pointer transition-colors hover:border-primary/50', className)}
      onClick={onClick}
    >
      <CardContent className="flex items-start gap-3 p-4">
        <Checkbox
          checked={isDone}
          onCheckedChange={(checked) => {
            onToggle?.(!!checked);
          }}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <p className={cn('text-sm font-medium', isDone && 'line-through text-muted-foreground')}>
            {task.title}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <TaskPriorityBadge priority={task.priority} />
            <TaskStatusBadge status={task.status} />
            {task.due_date && (
              <span
                className={cn(
                  'flex items-center gap-1 text-xs',
                  isOverdue ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                <Calendar className="h-3 w-3" />
                {new Date(task.due_date).toLocaleDateString()}
              </span>
            )}
            {task.assignee_count && task.assignee_count > 0 && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                {task.assignee_count}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
